import axios from 'axios';

const PROJECTS_URL =
  'https://services.arcgis.com/V3rkP5g6N5bDtF74/ArcGIS/rest/services/JCDD6_Projects/FeatureServer/0/query?where=1%3D1&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&resultType=none&distance=0.0&units=esriSRUnit_Meter&returnGeodetic=false&outFields=*&returnGeometry=true&featureEncoding=esriDefault&multipatchOption=xyFootprint&maxAllowableOffset=&geometryPrecision=&outSR=4326&datumTransformation=&applyVCSProjection=false&returnIdsOnly=false&returnUniqueIdsOnly=false&returnCountOnly=false&returnExtentOnly=false&returnQueryGeometry=false&returnDistinctValues=false&cacheHint=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&having=&resultOffset=&resultRecordCount=&returnZ=false&returnM=false&returnExceededLimitFeatures=true&quantizationParameters=&sqlFormat=none&f=pgeojson&token=';

export interface APIProperties {
  OBJECTID: number;
  ProjType?: string | null;
  ProjName?: string | null;
  Phase?: 'P' | 'D' | 'C' | 'F' | null;
  ConstructionEst?: string | null;
  CompletedDate?: string | null;
  Facility?: string | null;
  Watershed?: string | null;
  ProjNum?: string | null;
  Link?: string | null;
}

export type ProjectType = 'Maintenance' | 'Capital' | 'Grant';

export enum ProjectPhase {
  P = 'For Consideration',
  D = 'Design',
  C = 'Construction',
  F = 'Completed',
  U = 'Unknown',
}

// Extend `Record<string, unknown>` to satisfy `LocationList` typing
// TODO. Find better way of typing in `ProjectsList` component or fix in `LocationList`
export interface ProjectProperties extends Record<string, unknown> {
  id: string;
  nameWithProjectNumber: string;
  name?: string;
  projectType?: ProjectType;
  phase: ProjectPhase;
  estimatedConstructionCost?: string;
  completedDate?: string;
  facility: 'Yes' | 'No';
  watershed?: string;
  projectNumber?: string;
  detailsLink?: string;
}

export type ProjectFeature = GeoJSON.Feature<GeoJSON.Geometry, ProjectProperties>;
export type ProjectFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Geometry,
  ProjectProperties
>;

const currencyFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });

const mapFeature = (feature: GeoJSON.Feature<GeoJSON.Point, APIProperties>): ProjectFeature => {
  const props = feature.properties;

  return {
    ...feature,
    properties: {
      id: props.OBJECTID.toString(),
      nameWithProjectNumber: [props.ProjNum, props.ProjName].filter((v) => v).join(' - '),
      name: props.ProjName || undefined,
      projectType: (props.ProjType as ProjectType) || undefined,
      phase: props.Phase && ProjectPhase[props.Phase] ? ProjectPhase[props.Phase] : ProjectPhase.U,
      estimatedConstructionCost: props.ConstructionEst
        ? currencyFormatter.format(parseInt(props.ConstructionEst, 10))
        : undefined,
      completedDate: props.CompletedDate || undefined,
      facility: props.Facility === 'Y' ? 'Yes' : 'No',
      watershed: props.Watershed || undefined,
      projectNumber: props.ProjNum || undefined,
      detailsLink: props.Link || undefined,
    },
  };
};

const featureSort = (a: ProjectFeature, b: ProjectFeature): number => {
  // Sort by phase, then number - 1P, 2P, 3P, 10P, 1D, 2D, etc...

  const phaseOrder = {
    [ProjectPhase.P]: 0,
    [ProjectPhase.D]: 1,
    [ProjectPhase.C]: 2,
    [ProjectPhase.F]: 3,
    [ProjectPhase.U]: 4,
  };

  if (a.properties.phase === b.properties.phase) {
    // Use large number (1000) if no project number to place after all others
    const aNum = parseInt(a.properties.projectNumber || '1000', 10);
    const bNum = parseInt(b.properties.projectNumber || '1000', 10);
    return aNum - bNum;
  }

  return phaseOrder[a.properties.phase] - phaseOrder[b.properties.phase];
};

export const fetch = async (): Promise<ProjectFeatureCollection> => {
  const { data } = await axios.get<GeoJSON.FeatureCollection<GeoJSON.Point, APIProperties>>(
    PROJECTS_URL
  );

  const formatted = {
    ...data,
    features: data.features.map(mapFeature).sort(featureSort),
  };

  return formatted;
};
